"use client"

import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { useSelector, useDispatch } from "react-redux"
import Countdown from "react-countdown"
import { Mail, Phone, Lock, Leaf, LogOut, RefreshCw } from "lucide-react"
import { notify } from "../../../utility/Helper1"
import { userSelect } from "../../../redux/selectors/index"
import { authActions } from "../../../redux/actions"
import AuthHolder from "./AuthHolder";

const UserVerification = () => {
    const userData = useSelector(userSelect)
    const dispatch = useDispatch()
    const navigate = useNavigate()

    const [emailCodeInput, setEmailCodeInput] = useState("")
    const [phoneCodeInput, setPhoneCodeInput] = useState("")
    const [verifyingEmailOrPhone, setVerifyingEmailOrPhone] = useState(0)
    const [loading, changeLoading] = useState(false)
    const [loadingText, setLoadingText] = useState("")
    const [fullPageLoading, setFullPageLoading] = useState(false)

    // Verify email function
    const verifyEmail = (e) => {
        e.preventDefault()

        if (loading) {
            return
        }

        changeLoading(true)
        setLoadingText("Please wait...")

        dispatch(
            authActions.onSignUp("account/verify_email", { emailCode: emailCodeInput }, (response) => {
console.log('response ###', response)
                if (response.code == 1) {
                    if (userData?.emailVerification == 1 && userData?.phoneVerification == 1) {
                        navigate("/", { replace: true })
                    } else {
                        setVerifyingEmailOrPhone(1)
                    }
                }

                notify(response.msg, response.code)
                changeLoading(false)
                setLoadingText("")
            }),
        )
    }

    // Resend email verification
    const resendEmailVerification = (e) => {
        e.preventDefault()

        if (loading) {
            return
        }

        changeLoading(true)
        setLoadingText("Please wait...")

        dispatch(
            authActions.onRefreshSystemData("account/resend_email_verification", 100, {}, (response) => {
                notify(response.msg, response.code)
                changeLoading(false)
                setLoadingText("")
            }),
        )
    }

    // Resend phone verification
    const resendPhoneVerification = (e) => {
        e.preventDefault()

        if (loading) {
            return
        }

        changeLoading(true)
        setLoadingText("Please wait...")

        dispatch(
            authActions.onRefreshSystemData("account/resend_phone_verification", 100, {}, (response) => {
                notify(response.msg, response.code)
                setFullPageLoading(false)
                changeLoading(false)
            }),
        )
    }

    // Verify phone function
    const verifyPhone = (e) => {
        e.preventDefault()

        if (loading) {
            return
        }

        changeLoading(true)
        setLoadingText("Please wait...")

        dispatch(
            authActions.onSignUp("account/verify_phone", { smsCode: phoneCodeInput }, (response) => {
                if (response.code == 1) {
                    navigate("/", { replace: true })
                }

                notify(response.msg, response.code)
                changeLoading(false)
                setLoadingText("")
            }),
        )
    }

    // Logout function
    const doLogOutNowNow = () => {
        dispatch(authActions.onLogout())
    }

    // Set verification type based on user data
    useEffect(() => {
        if (userData?.emailVerification == 0) {
            setVerifyingEmailOrPhone(0)
        } else if (userData?.phoneVerification == 0) {
            setVerifyingEmailOrPhone(1)
        }
    }, [userData])

    return (
        <AuthHolder>

            <div className="space-y-1 p-6 pb-4">
                <h2 className="text-2xl font-bold text-center text-green-800">
                    {verifyingEmailOrPhone === 0 ? "Email Verification" : "Phone Verification"}
                </h2>
                <p className="text-center text-green-600">
                    {verifyingEmailOrPhone === 0
                        ? `We sent a verification code to ${userData?.email}`
                        : `We sent an OTP to (${userData?.countryPhoneCode})${userData?.phone}`}
                </p>
            </div>

            <div className="p-6 pt-2">
                {verifyingEmailOrPhone === 0 ? (
                    <form onSubmit={verifyEmail} className="space-y-4">
                        <div className="space-y-2">
                            <label htmlFor="email-code" className="block text-green-700 font-medium">
                                Verification Code
                            </label>
                            <div className="relative">
                                <Lock className="absolute left-3 top-2.5 h-5 w-5 text-green-500" />
                                <input
                                    id="email-code"
                                    type="text"
                                    placeholder="Enter verification code"
                                    value={emailCodeInput}
                                    onChange={(e) => setEmailCodeInput(e.target.value)}
                                    required
                                    className="w-full pl-10 pr-3 py-2 border border-green-200 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all text-center font-mono text-lg tracking-wider"
                                />
                            </div>
                        </div>

                        <div className="mt-6 flex flex-col space-y-4">
                            <button
                                type="submit"
                                className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-2.5 px-4 rounded-md transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-70 disabled:cursor-not-allowed"
                                disabled={loading}
                            >
                                {loading ? (
                                    <div className="flex items-center justify-center">
                                        <svg
                                            className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                        >
                                            <circle
                                                className="opacity-25"
                                                cx="12"
                                                cy="12"
                                                r="10"
                                                stroke="currentColor"
                                                strokeWidth="4"
                                            ></circle>
                                            <path
                                                className="opacity-75"
                                                fill="currentColor"
                                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                            ></path>
                                        </svg>
                                        Verifying...
                                    </div>
                                ) : (
                                    "Verify Email"
                                )}
                            </button>

                            <div className="flex items-center justify-between pt-2">
                                <div className="text-sm text-green-700">
                                    <Countdown
                                        zeroPadTime={2}
                                        key={new Date(userData?.lastEmailTime)}
                                        date={new Date(userData?.lastEmailTime)}
                                        renderer={({ hours, minutes, seconds, completed }) => {
                                            if (completed) {
                                                return (
                                                    <button
                                                        onClick={resendEmailVerification}
                                                        disabled={loading}
                                                        className="flex items-center text-green-600 hover:text-green-800 font-medium"
                                                    >
                                                        <RefreshCw className="h-3 w-3 mr-1" />
                                                        Resend Code
                                                        {loading && (
                                                            <svg
                                                                className="animate-spin ml-2 h-3 w-3"
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                fill="none"
                                                                viewBox="0 0 24 24"
                                                            >
                                                                <circle
                                                                    className="opacity-25"
                                                                    cx="12"
                                                                    cy="12"
                                                                    r="10"
                                                                    stroke="currentColor"
                                                                    strokeWidth="4"
                                                                ></circle>
                                                                <path
                                                                    className="opacity-75"
                                                                    fill="currentColor"
                                                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                                                ></path>
                                                            </svg>
                                                        )}
                                                    </button>
                                                )
                                            } else {
                                                return (
                                                    <span className="flex items-center">
                                                        <RefreshCw className="h-3 w-3 mr-1 text-green-500" />
                                                        Resend in{" "}
                                                        <span className="font-mono ml-1">
                                                            {minutes}:{seconds < 10 ? `0${seconds}` : seconds}
                                                        </span>
                                                    </span>
                                                )
                                            }
                                        }}
                                    />
                                </div>

                                <button
                                    type="button"
                                    onClick={doLogOutNowNow}
                                    className="flex items-center text-green-600 hover:text-green-800 font-medium"
                                >
                                    <LogOut className="h-3 w-3 mr-1" />
                                    Sign Out
                                </button>
                            </div>
                        </div>
                    </form>
                ) : (
                    <form onSubmit={verifyPhone} className="space-y-4">
                        <div className="space-y-2">
                            <label htmlFor="phone-code" className="block text-green-700 font-medium">
                                OTP Code
                            </label>
                            <div className="relative">
                                <Lock className="absolute left-3 top-2.5 h-5 w-5 text-green-500" />
                                <input
                                    id="phone-code"
                                    type="text"
                                    placeholder="Enter OTP code"
                                    value={phoneCodeInput}
                                    onChange={(e) => setPhoneCodeInput(e.target.value)}
                                    required
                                    className="w-full pl-10 pr-3 py-2 border border-green-200 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all text-center font-mono text-lg tracking-wider"
                                />
                            </div>
                        </div>

                        <div className="mt-6 flex flex-col space-y-4">
                            <button
                                type="submit"
                                className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-2.5 px-4 rounded-md transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-70 disabled:cursor-not-allowed"
                                disabled={loading}
                            >
                                {loading ? (
                                    <div className="flex items-center justify-center">
                                        <svg
                                            className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                        >
                                            <circle
                                                className="opacity-25"
                                                cx="12"
                                                cy="12"
                                                r="10"
                                                stroke="currentColor"
                                                strokeWidth="4"
                                            ></circle>
                                            <path
                                                className="opacity-75"
                                                fill="currentColor"
                                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                            ></path>
                                        </svg>
                                        Verifying...
                                    </div>
                                ) : (
                                    "Verify Phone"
                                )}
                            </button>

                            <div className="flex items-center justify-between pt-2">
                                <div className="text-sm text-green-700">
                                    <Countdown
                                        zeroPadTime={2}
                                        key={new Date(userData?.lastSMSTime)}
                                        date={new Date(userData?.lastSMSTime)}
                                        renderer={({ hours, minutes, seconds, completed }) => {
                                            if (completed) {
                                                return (
                                                    <button
                                                        onClick={resendPhoneVerification}
                                                        disabled={loading}
                                                        className="flex items-center text-green-600 hover:text-green-800 font-medium"
                                                    >
                                                        <RefreshCw className="h-3 w-3 mr-1" />
                                                        Resend OTP
                                                        {loading && (
                                                            <svg
                                                                className="animate-spin ml-2 h-3 w-3"
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                fill="none"
                                                                viewBox="0 0 24 24"
                                                            >
                                                                <circle
                                                                    className="opacity-25"
                                                                    cx="12"
                                                                    cy="12"
                                                                    r="10"
                                                                    stroke="currentColor"
                                                                    strokeWidth="4"
                                                                ></circle>
                                                                <path
                                                                    className="opacity-75"
                                                                    fill="currentColor"
                                                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                                                ></path>
                                                            </svg>
                                                        )}
                                                    </button>
                                                )
                                            } else {
                                                return (
                                                    <span className="flex items-center">
                                                        <RefreshCw className="h-3 w-3 mr-1 text-green-500" />
                                                        Resend in{" "}
                                                        <span className="font-mono ml-1">
                                                            {minutes}:{seconds < 10 ? `0${seconds}` : seconds}
                                                        </span>
                                                    </span>
                                                )
                                            }
                                        }}
                                    />
                                </div>

                                <button
                                    type="button"
                                    onClick={doLogOutNowNow}
                                    className="flex items-center text-green-600 hover:text-green-800 font-medium"
                                >
                                    <LogOut className="h-3 w-3 mr-1" />
                                    Sign Out
                                </button>
                            </div>
                        </div>
                    </form>
                )}
            </div>

            <div className="p-6 pt-0">
                <div className="p-4 bg-green-50 border border-green-200 rounded-md">
                    <div className="flex items-start">
                        <div className="flex-shrink-0">
                            {verifyingEmailOrPhone === 0 ? (
                                <Mail className="h-5 w-5 text-green-500" />
                            ) : (
                                <Phone className="h-5 w-5 text-green-500" />
                            )}
                        </div>
                        <div className="ml-3">
                            <h3 className="text-sm font-medium text-green-800">Verification Required</h3>
                            <div className="mt-1 text-sm text-green-700">
                                <small>
                                    {verifyingEmailOrPhone === 0
                                        ? "You may need to check your spam folder."
                                        : "Please check your phone for the SMS containing your verification code."}
                                </small>
                            </div>
                        </div>
                    </div>
                </div>
            </div>



        </AuthHolder>
    )
}

export default UserVerification
